/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  AbstractRestService,
  identity,
  none,
  optimisticLockingOf,
  privilegedTokenOf,
  RestSettings,
} from '@gh/core-data';
import { currentDate } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import {
  AccountingMigrationDataDto,
  SystemVarsAccountingExpenseDto,
  SystemVarsAccountingJobs,
  SystemVarsAccountingJobsDto,
  SystemVarsAccountingReceivableDto,
  SystemVarsAccountsTwilioDto,
  SystemVarsAccountsXeroDto,
  SystemVarsAccountsXeroSyncDto,
  SystemVarsAccountsXeroUrlDto,
  SystemVarsContactsDto,
  SystemVarsFranchiseeDto,
  SystemVarsJobDto,
  SystemVarsLedgerDto,
  SystemVarsManagerDto,
  SystemVarsManagerStatisticDto,
  SystemVarsMasterZoneDetailsDto,
  SystemVarsPayablesDto,
  SystemVarsReceivablesDto,
  SystemVarsSupervisorDto,
  XeroSyncProgressDto,
} from './system-vars.dto';
import {
  AccountingMigrationDataMapper,
  SystemVarsAccountingExpenseMapper,
  SystemVarsAccountingJobsMapper,
  SystemVarsAccountingReceivableMapper,
  SystemVarsAccountsTwilioMapper,
  SystemVarsAccountsXeroMapper,
  SystemVarsAccountsXeroSyncMapper,
  SystemVarsAccountsXeroUrlMapper,
  SystemVarsContactsMapper,
  SystemVarsFranchiseeMapper,
  SystemVarsJobMapper,
  SystemVarsLedgerMapper,
  SystemVarsManagerMapper,
  SystemVarsMasterZoneDetailsMapper,
  SystemVarsPayablesMapper,
  SystemVarsReceivablesMapper,
  SystemVarsSupervisorMapper,
} from './system-vars.mapper';

@Injectable()
export class SystemVarsRestService extends AbstractRestService {

  /**
   *
   * @returns {{id: number, subject: string, message: string, assignee: any, status: any, dueDate: Date}}
   */
  static prepareFranchiseeForCreate(): SystemVarsFranchiseeDto {
    return {
      id: void 0,
      email: '',
      typeOfBusiness: '',
      formedDate: currentDate(),
      companyName: '',
      displayName: '',
      usersFromOtherOfficesAccess: false,
      isUsersFromOtherOfficesAccessActive: true,
      usersEmailAccessFromOtherOffices: false,
      isUsersEmailAccessFromOtherOfficesActive: true,
      systemName: '',
      tradingAs: '',
      abn: '',
      acn: '',
      gst: '',
      taxId: void 0,
      street1: '',
      street2: '',
      city: '',
      state: '',
      postCode: '',
      licenseNo: '',
      phoneOffice: '',
      fax: '',
      phoneMobile: '',
      publishToWeb: false,
      isPublishToWebAvailable: false,
      billing: {
        after36Months: 0,
        assignmentFee: 0,
        contractSignedDate: currentDate(),
        currentAgreement: currentDate(),
        depositDate: currentDate(),
        franchiseFee: 0,
        initialFranchiseFee: 0,
        officeClosed: currentDate(),
        onSellDate: currentDate(),
        originalCommencement: currentDate(),
        renewalDate: currentDate(),
        saleAmount: 0,
        taxRate: 0,
        term: 0,
        within18Months: 0,
        within24Months: 0,
        within36Months: 0,
      },
    };
  }

  constructor(settings: RestSettings) {
    super(settings);
  }

  /**
   * @param franchisee
   * @returns {Observable<SystemVarsFranchiseeDto>}
   */
  updateFranchisee(franchisee: SystemVarsFranchiseeDto): Observable<SystemVarsFranchiseeDto> {
    return this.httpPut(
      `${this.bp}/system-variables/franchisee`,
      franchisee,
      privilegedTokenOf(optimisticLockingOf(SystemVarsFranchiseeMapper)));
  }

  /**
   *
   * @returns {Observable<SystemVarsFranchiseeDto>}
   */
  getFranchisee(): Observable<SystemVarsFranchiseeDto> {
    return this.httpGet(
      `${this.bp}/system-variables/franchisee`,
      privilegedTokenOf(optimisticLockingOf(SystemVarsFranchiseeMapper)));
  }

  getMasterZoneInfo(): Observable<SystemVarsMasterZoneDetailsDto> {
    return this.httpGet(`${this.bp}/system-variables/master-zone`, SystemVarsMasterZoneDetailsMapper);
  }

  getContactSettings(): Observable<SystemVarsContactsDto> {
    return this.httpGet(
      `${this.bp}/system-variables/contact-settings`,
      optimisticLockingOf(SystemVarsContactsMapper));
  }

  updateContactSettings(contactSettings: SystemVarsContactsDto): Observable<SystemVarsContactsDto> {
    return this.httpPut(
      `${this.bp}/system-variables/contact-settings`,
      contactSettings,
      optimisticLockingOf(SystemVarsContactsMapper));
  }

  getAccountsTwilioSettings(): Observable<SystemVarsAccountsTwilioDto> {
    return this.httpGet(
      `${this.bp}/system-variables/integration-accounts/sms`,
      optimisticLockingOf(SystemVarsAccountsTwilioMapper));
  }

  updateAccountsTwilioSettings(settings: SystemVarsAccountsTwilioDto): Observable<SystemVarsAccountsTwilioDto> {
    return this.httpPut(
      `${this.bp}/system-variables/integration-accounts/sms`,
      settings,
      optimisticLockingOf(SystemVarsAccountsTwilioMapper));
  }

  getAccountsXeroSettings(): Observable<SystemVarsAccountsXeroDto> {
    return this.httpGet(
      `${this.accounting}/system-variables/integration-accounts/accounting/auth2`,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
    );
  }

  getXeroAcceptUrl(settings: SystemVarsAccountsXeroDto): Observable<SystemVarsAccountsXeroUrlDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/integration-accounts/accounting/auth2/generate-accept-url`,
      settings,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
      SystemVarsAccountsXeroUrlMapper,
    );
  }

  connectAccountsXero(authCode: string, organizationName?: string, state?: string): Observable<SystemVarsAccountsXeroDto> {
    const payload: any = { authCode };
    if (organizationName) {
      payload.organizationName = organizationName;
    }
    if (state) {
      payload.state = state;
    }
    return this.httpPut(
      `${this.accounting}/system-variables/integration-accounts/accounting/auth2/connect-to-xero`,
      payload,
      identity(),
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
    );
  }

  modifyAccountsXero(settings: SystemVarsAccountsXeroDto): Observable<SystemVarsAccountsXeroUrlDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/integration-accounts/accounting/auth2/modify-xero-integration-account`,
      settings,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
      SystemVarsAccountsXeroUrlMapper,
    );
  }

  getAccountsXeroWebhookUrl(): Observable<SystemVarsAccountsXeroDto> {
    return this.httpGet(
      `${this.accounting}/system-variables/integration-accounts/accounting/auth2/generate-webhook-url`,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
    );
  }

  syncAccountsXero(options: SystemVarsAccountsXeroSyncDto): Observable<void> {
    return this.httpPost(
      `${this.accounting}/system-variables/integration-accounts/accounting/sync`,
      options,
      SystemVarsAccountsXeroSyncMapper,
      none(),
    );
  }

  saveXeroWebhookKey(request: any): Observable<SystemVarsAccountsXeroDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/integration-accounts/accounting/auth2/save-webhook-key`,
      request,
      optimisticLockingOf(identity()),
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
    );
  }

  restartXeroSync(): Observable<void> {
    return this.httpPut(`${this.accounting}/system-variables/integration-accounts/accounting/restart`, void 0, none());
  }

  migrateAccountsXeroToConsolidated(settings?: any): Observable<SystemVarsAccountsXeroDto> {
    return this.httpPost(
       `${this.accounting}/system-variables/integration-accounts/accounting/auth2/migrate`,
      settings || {},
      privilegedTokenOf(identity()),
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountsXeroMapper)),
    );
  }

  startAccountingSync(data: AccountingMigrationDataDto): Observable<void> {
    return this.httpPut(
      `${this.bp}/system-variables/integration-accounts/accounting/sync-migrated`,
      data,
      AccountingMigrationDataMapper,
      none());
  }

  checkSyncProgress(): Observable<XeroSyncProgressDto> {
    return this.httpGet(`${this.accounting}/system-variables/integration-accounts/accounting/check-sync-progress`, identity());
  }

  getJob(): Observable<SystemVarsJobDto> {
    return this.httpGet(
      `${this.bp}/system-variables/jobs`,
      optimisticLockingOf(SystemVarsJobMapper),
    );
  }

  saveJob(job: SystemVarsJobDto): Observable<SystemVarsJobDto> {
    return this.httpPut(
      `${this.bp}/system-variables/jobs`,
      job,
      privilegedTokenOf(optimisticLockingOf(SystemVarsJobMapper)),
    );
  }

  getReceivables(): Observable<SystemVarsReceivablesDto> {
    return this.httpGet(`${this.bp}/system-variables/receivables`, optimisticLockingOf(SystemVarsReceivablesMapper));
  }

  updateReceivables(receivables: SystemVarsReceivablesDto): Observable<SystemVarsReceivablesDto> {
    return this.httpPut(`${this.bp}/system-variables/receivables`, receivables, optimisticLockingOf(SystemVarsReceivablesMapper));
  }

  getManager(): Observable<SystemVarsManagerDto> {
    return this.httpGet(`${this.bp}/system-variables/manager`, optimisticLockingOf(SystemVarsManagerMapper));
  }

  updateManager(manager: SystemVarsManagerDto): Observable<SystemVarsManagerDto> {
    return this.httpPut(`${this.bp}/system-variables/manager`, manager, optimisticLockingOf(SystemVarsManagerMapper));
  }

  getManagerStatistic(): Observable<SystemVarsManagerStatisticDto> {
    return this.httpGet(`${this.bp}/system-variables/manager/show-available`, identity());
  }

  getSupervisor(): Observable<SystemVarsSupervisorDto> {
    return this.httpGet(
      `${this.bp}/system-variables/supervisor`,
      optimisticLockingOf(SystemVarsSupervisorMapper),
    );
  }

  updateSupervisor(supervisor: SystemVarsSupervisorDto): Observable<SystemVarsSupervisorDto> {
    return this.httpPut(
      `${this.bp}/system-variables/supervisor`,
      supervisor,
      optimisticLockingOf(SystemVarsSupervisorMapper),
    );
  }

  getPayables(): Observable<SystemVarsPayablesDto> {
    return this.httpGet(
      `${this.bp}/system-variables/payables`,
      optimisticLockingOf(SystemVarsPayablesMapper),
    );
  }

  updatePayables(payables: SystemVarsPayablesDto): Observable<SystemVarsPayablesDto> {
    return this.httpPut(
      `${this.bp}/system-variables/payables`,
      payables,
      optimisticLockingOf(SystemVarsPayablesMapper),
    );
  }

  getLedger(): Observable<SystemVarsLedgerDto> {
    return this.httpGet(
      `${this.bp}/system-variables/general-ledger`,
      optimisticLockingOf(SystemVarsLedgerMapper),
    );
  }

  updateLedger(ledger: SystemVarsLedgerDto): Observable<SystemVarsLedgerDto> {
    delete ledger.gstForIncome;
    return this.httpPut(
      `${this.bp}/system-variables/general-ledger`,
      ledger,
      optimisticLockingOf(SystemVarsLedgerMapper),
    );
  }

  getAccountingReceivables(): Observable<SystemVarsAccountingReceivableDto> {
    return this.httpGet(
      `${this.accounting}/system-variables/accounting/receivables`,
      optimisticLockingOf(SystemVarsAccountingReceivableMapper),
    );
  }

  updateAccountingReceivables(accounting: SystemVarsAccountingReceivableDto):
    Observable<SystemVarsAccountingReceivableDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/accounting/receivables`,
      accounting,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountingReceivableMapper)),
    );
  }

  modifyAccountingReceivables(accounting: SystemVarsAccountingReceivableDto):
    Observable<SystemVarsAccountingReceivableDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/accounting/receivables/modify`,
      accounting,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountingReceivableMapper)),
    );
  }

  getAccountingJobs(type: SystemVarsAccountingJobs): Observable<SystemVarsAccountingJobsDto> {
    return this.httpGet(
      `${this.accounting}/system-variables/accounting/${type}`,
      optimisticLockingOf(SystemVarsAccountingJobsMapper),
    );
  }

  updateAccountingJobs(type: SystemVarsAccountingJobs, accounting: SystemVarsAccountingJobsDto):
    Observable<SystemVarsAccountingJobsDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/accounting/${type}`,
      accounting,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountingJobsMapper)),
    );
  }

  getAccountingExpense(): Observable<SystemVarsAccountingExpenseDto> {
    return this.httpGet(
      `${this.accounting}/system-variables/accounting/expense`,
      optimisticLockingOf(SystemVarsAccountingExpenseMapper),
    );
  }

  updateAccountingExpense(accounting: SystemVarsAccountingExpenseDto):
    Observable<SystemVarsAccountingExpenseDto> {
    return this.httpPut(
      `${this.accounting}/system-variables/accounting/expense`,
      accounting,
      privilegedTokenOf(optimisticLockingOf(SystemVarsAccountingExpenseMapper)),
    );
  }

  getAccountingMigrationData(): Observable<AccountingMigrationDataDto> {
    return this.httpGet(`${this.bp}/system-variables/integration-accounts/accounting/migrated-data`,
                        AccountingMigrationDataMapper);
  }

  modifyColorScheme(colorScheme: string): Observable<string> {
    return this.httpPut(`${this.bp}/color-scheme/modify`, { colorScheme }, identity())
      .pipe(map$(({ colorScheme: updatedColorScheme }) => updatedColorScheme));
  }

  /**
   * Creates Stripe subscription session for customer portal
   * @returns {Observable<{customerPortalSessionUrl: string}>}
   */
  createStripeSubscriptionSession(officeCode?: string): Observable<{customerPortalSessionUrl: string}> {
    const query = officeCode ? `?officeCode=${encodeURIComponent(officeCode)}` : '';
    return this.httpGet(`${this.bp}/stripe/subscriptions/session${query}`, identity());
  }

  /**
   * Fetches the invoice payment link for unpaid invoices
   * @returns {Observable<{invoiceUrl: string}>}
   */
  getInvoiceLink(): Observable<{invoiceUrl: string}> {
    return this.httpGet(`${this.bp}/stripe/subscriptions/invoice-url`, identity());
  }

  /**
   * Toggles Stripe status for the region
   * @returns {Observable<{isStripeEnabled: boolean}>}
   */
  toggleStripeRegionStatus(isStripeEnabled: boolean): Observable<{isStripeEnabled: boolean}> {
    return this.httpPut(`${this.bp}/stripe/isEnabled`, { isStripeEnabled }, identity());
  }
}
